import { useState, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

type PanelPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'bottom-center';

interface FloatingPanelProps {
  title: string;
  position: PanelPosition;
  children: ReactNode;
  defaultCollapsed?: boolean;
  className?: string;
  icon?: ReactNode;
  highlightId?: string;
}

const positionClasses: Record<PanelPosition, string> = {
  'top-left': 'top-3 left-3',
  'top-right': 'top-3 right-3',
  'bottom-left': 'bottom-3 left-3',
  'bottom-right': 'bottom-3 right-14',
  'bottom-center': 'bottom-3 left-1/2 -translate-x-1/2',
};

export function FloatingPanel({
  title,
  position,
  children,
  defaultCollapsed = false,
  className = '',
  icon,
  highlightId,
}: FloatingPanelProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div className={`absolute z-10 ${positionClasses[position]} ${className}`} {...(highlightId ? { 'data-highlight': highlightId } : {})}>
      <div className="bg-gray-900/90 backdrop-blur-md border border-gray-700/50 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-800/50 transition-colors"
        >
          {icon}
          <span className="text-xs font-medium text-gray-300 font-mono">{title}</span>
          <ChevronDown
            className={`w-3 h-3 text-gray-500 ml-auto transition-transform ${collapsed ? '-rotate-90' : ''}`}
          />
        </button>

        {/* Content */}
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-gray-700/50">
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
